import React, { useRef, useState, useEffect } from 'react'
import bootstrapScopedStyles from '../../styles/bootstrapScoped.min.module.css'
import formStyles from './../../branding/formStyles'
import customFormStyles from '../../styles/formStyles.module.css'

export default function OnfsCheckGroup({
  name,
  label,
  indent = '15px',
  children,
  style,
  inline = false,
  selectAll = true,
  requireOne = false,
  wasSubmitted = false,
  setPreventSubmit
}) {
  const parentCheckboxRef = useRef()
  const childrenContainerRef = useRef()
  const [checkboxOpacity, setCheckboxOpacity] = useState('1')
  const [oneSelected, setOneSelected] = useState(false)
  const [triggerChange, setTriggerChange] = useState(false)
  const [wasTouched, setWasTouched] = useState(false)

  const childrenStyle = inline
    ? { ...formStyles.inlineContainer, ...style }
    : style

  useEffect(() => {
    // Check for require one selection error
    if (requireOne) {
      const checkContainers =
        childrenContainerRef.current.getElementsByTagName('input')

      const atLeastOneSelected = numberSelected(checkContainers) > 0

      setOneSelected(atLeastOneSelected)
      setPreventSubmit(!atLeastOneSelected)
    } else {
      setPreventSubmit(false)
    }

    // Set parent check opacity
    if (selectAll) {
      const childrenCheckBoxes =
        childrenContainerRef.current.getElementsByTagName('input')

      const someUnselected =
        numberSelected(childrenCheckBoxes) < childrenCheckBoxes.length
      const isParentChecked = parentCheckboxRef.current.checked
      if (!isParentChecked) {
        setCheckboxOpacity('1')
      } else if (isParentChecked && someUnselected) {
        setCheckboxOpacity('.4')
      } else if (isParentChecked && !someUnselected) {
        setCheckboxOpacity('1')
      }
    }
  }, [triggerChange])

  return (
    <div>
      <fieldset
        aria-describedby={
          requireOne && !oneSelected && (wasSubmitted || wasTouched)
            ? name + '-error'
            : null
        }
        aria-invalid={
          requireOne && !oneSelected && (wasSubmitted || wasTouched)
            ? 'true'
            : 'false'
        }
      >
        {selectAll && (
          <div className={bootstrapScopedStyles['form-check']}>
            <div
              style={{ cursor: 'default' }}
              onClick={() => {
                parentCheckboxRef.current.click()
              }}
            >
              <input
                onClick={(e) => {
                  e.stopPropagation()
                  doSelectUnselectAll(childrenContainerRef, parentCheckboxRef)
                }}
                id={name}
                ref={parentCheckboxRef}
                type='checkbox'
                className={bootstrapScopedStyles['form-check-input']}
                style={{ opacity: `${checkboxOpacity}` }}
              />
              <label hidden htmlFor={name}>
                Select all options for {label}
              </label>
              <legend
                className={`${bootstrapScopedStyles['form-check-label']} ${
                  requireOne ? customFormStyles.required : ''
                }`}
                style={{ ...formStyles.legend, marginBottom: '8px' }}
              >
                {label}
              </legend>
            </div>
          </div>
        )}

        {!selectAll && (
          <legend
            className={`${requireOne ? customFormStyles.required : ''}`}
            style={formStyles.legend}
          >
            {label}
          </legend>
        )}
        <RenderChildrenCheckboxes
          name={name}
          children={children}
          childrenContainerRef={childrenContainerRef}
          indent={indent}
          style={childrenStyle}
          doOnChange={() => {
            setTriggerChange(!triggerChange)
            setWasTouched(true)
          }}
        />

        {requireOne && !oneSelected && (wasSubmitted || wasTouched) && (
          <div
            role='alert'
            id={name + '-error'}
            className={`${bootstrapScopedStyles.alert} ${bootstrapScopedStyles['alert-danger']}`}
          >
            At least one option is required
          </div>
        )}
      </fieldset>
    </div>
  )
}

function RenderChildrenCheckboxes({
  name,
  children,
  childrenContainerRef,
  indent,
  style,
  doOnChange
}) {
  return (
    <div
      onChange={() => doOnChange()}
      ref={childrenContainerRef}
      style={{ marginLeft: indent, ...style }}
    >
      {React.Children.map(children, (child) => (
        <div key={`${name}_${child.props.name}`}>{child}</div>
      ))}
    </div>
  )
}

function numberSelected(checkContainers) {
  let numSelected = 0
  for (let i = 0; i < checkContainers.length; i++) {
    if (checkContainers[i].value.toLowerCase() === 'true') {
      numSelected++
    }
  }
  return numSelected
}

function doSelectUnselectAll(childrenContainerRef, parentCheckboxRef) {
  const childrenCheckBoxes =
    childrenContainerRef.current.getElementsByTagName('input')
  // Click children checkboxes to match parent
  for (let i = 0; i < childrenCheckBoxes.length; i++) {
    if (childrenCheckBoxes[i].checked !== parentCheckboxRef.current.checked) {
      childrenCheckBoxes[i].click()
    }
  }
}
